import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  clearAnalistaCuenta,
  getAnalistaCuenta,
  getCuentaCorriente,
  getCuentaCorrienteWithFilter,
} from "../../redux/actions/cuentaCorriente";
import { getDatosContractuales } from "../../redux/actions/informacion";
import { convertDateTimeToNet } from "../../util/datetime";
import CircularLoading from "../commons/loading/CircularLoading";
import TabBar from "../commons/tabBar/TabBar";
import { TipoContratosEnum } from "../enums/tipoContratosEnum";
import { StyledCuentaCorrientePage } from "../styled/CuentaCorriente";
import CuentaCorrienteTable from "./CuentaCorrienteTable";

const CuentaCorrienteContainer = () => {
  const dispatch = useDispatch();
  const { cuentaCorriente, analistaDeCuenta } = useSelector(
    (state) => state.cuentaCorriente
  );
  const datosContractuales = useSelector(
    (state) => state.informacion.datosContractuales
  );

  const [fechaDesde, setFechaDesde] = useState(null);
  const [fechaHasta, setFechaHasta] = useState(null);
  const [selectedTab, setSelectedTab] = useState(TipoContratosEnum["Régimen General"]);

  const tabOptions = useMemo(() => {
    if (!datosContractuales.data?.mixto) {
      return [];
    }
    return Object.entries(TipoContratosEnum).map(([label, value]) => ({
      label,
      value,
    }));
  }, [datosContractuales.data?.mixto]);

  const tableData = useMemo(() => {
    if (!cuentaCorriente.data || Object.keys(cuentaCorriente.data).length === 0) {
      return [];
    }

    let result;
    if (datosContractuales.data?.mixto) {
      result = selectedTab === TipoContratosEnum["Domésticas"]
        ? cuentaCorriente.data.visualCuentasCorrientesDomesticas
        : cuentaCorriente.data.visualCuentasCorrientesRegimenGeneral;
    } else {
      result = cuentaCorriente.data.visualCuentasCorrientes;
    }

    return result ?? [];
  }, [cuentaCorriente.data, selectedTab, datosContractuales.data?.mixto]);

  const balanceAtDate = useMemo(() => {
    if (!cuentaCorriente.data || Object.keys(cuentaCorriente.data).length === 0) {
      return 0;
    }

    let result;
    if (datosContractuales.data?.mixto) {
      result = selectedTab === TipoContratosEnum["Domésticas"]
        ? cuentaCorriente.data.saldoFechaDomestica
        : cuentaCorriente.data.saldoFechaRegimenGeneral;
    } else {
      result = cuentaCorriente.data.saldoFecha;
    }

    return result ?? 0;
  }, [cuentaCorriente.data, selectedTab, datosContractuales.data?.mixto]);

  useEffect(() => {
    dispatch(getCuentaCorriente());
    dispatch(getDatosContractuales());
    dispatch(getAnalistaCuenta());
    return () => {
      dispatch(clearAnalistaCuenta());
    };
  }, [dispatch]);

  const handleFilter = (values) => {
    dispatch(
      getCuentaCorrienteWithFilter(
        convertDateTimeToNet(values.fechaDesde),
        convertDateTimeToNet(values.fechaHasta)
      )
    );

    setFechaDesde(values.fechaDesde);
    setFechaHasta(values.fechaHasta);
  };

  const refreshData = () => {
    dispatch(getCuentaCorriente());
  };

  const handleTabChange = (tab) => {
    setSelectedTab(tab);
  };

  return (
    <>
      {datosContractuales.loading ? (
        <CircularLoading />
      ) : (
        <div>
          {tabOptions.length > 1 && (
            <StyledCuentaCorrientePage.TabBarContainer>
              <TabBar
                options={tabOptions}
                value={selectedTab}
                onChange={handleTabChange}
              />
            </StyledCuentaCorrientePage.TabBarContainer>
          )}
          <CuentaCorrienteTable
            title="Cuenta Corriente"
            data={tableData}
            fetchData={refreshData}
            balanceAtDate={balanceAtDate}
            noRefresh={false}
            secondaryTitle={true}
            loading={cuentaCorriente.loading || analistaDeCuenta.loading}
            handleFilter={handleFilter}
            fechaDesde={fechaDesde}
            fechaHasta={fechaHasta}
            selectedTab={datosContractuales.data?.mixto ? selectedTab : undefined}
          />
        </div>
      )}
    </>
  );
};

export default CuentaCorrienteContainer;
